import { createSelector } from "@reduxjs/toolkit";
import {
  IResult,
  ITrackingProperties,
  BaseActivitiesProperties,
  CallState,
  ActivitiesEntryTypeEnum,
} from "redmond";
import { IStoreState } from "../../../../reducers/types";
import { getRewardsAccounts } from "../../../rewards/reducer";
import {
  getFetchTravelWalletDetailsCallState,
  getTravelWalletCredit,
} from "../../../travel-wallet/reducer";
import {
  CombinedSearchGroup,
  CombinedSearchGroupType,
  MAX_RESULTS_PER_GROUP,
} from "../../types";

export const getLocation = (state: IStoreState): IResult | null =>
  state.experiencesSearch.location;

export const getLocationCategories = (state: IStoreState) =>
  state.experiencesSearch.locationCategories;

export const getLocationCategoriesLoading = (state: IStoreState) =>
  state.experiencesSearch.locationCategoriesLoading;

export const getHasLocationAutocompleteError = (state: IStoreState) =>
  state.experiencesSearch.hasLocationAutocompleteError;

export const getFromDate = (state: IStoreState) =>
  state.experiencesSearch.fromDate;

export const getUntilDate = (state: IStoreState) =>
  state.experiencesSearch.untilDate;

export const getCategoryTagId = (state: IStoreState) =>
  state.experiencesSearch.categoryTagId;

export const getCategoryTagKind = (state: IStoreState) =>
  state.experiencesSearch.categoryTagKind;

export const getEntryType = (state: IStoreState) =>
  state.experiencesSearch.entryType;

export const getEntryTypesAll = (state: IStoreState) =>
  state.experiencesSearch.entryTypesAll;

export const getLodgingId = (state: IStoreState) =>
  state.experiencesSearch.lodgingId;

export const getStaysAdvance = (state: IStoreState) =>
  state.experiencesSearch.staysAdvance;

// Trip destinations selectors
export const getTripDestinations = (state: IStoreState) =>
  state.experiencesSearch.tripDestinations;

export const getTripDestinationsLoading = (state: IStoreState) =>
  state.experiencesSearch.tripDestinationsLoading;

export const getTripDestinationsError = (state: IStoreState) =>
  state.experiencesSearch.tripDestinationsError;

export const getTripDestinationsLoaded = (state: IStoreState) =>
  state.experiencesSearch.tripDestinationsLoaded;

// Combined autocomplete selectors
export const getCombinedAutocompleteGroups = (state: IStoreState) =>
  state.experiencesSearch.combinedAutocompleteGroups ?? [];

export const getCombinedAutocompleteLoading = (state: IStoreState) =>
  state.experiencesSearch.combinedAutocompleteLoading;

export const getHasCombinedAutocompleteError = (state: IStoreState) =>
  state.experiencesSearch.hasCombinedAutocompleteError;

/**
 * Truncates each group to MAX_RESULTS_PER_GROUP and excludes Collection.
 * Category and Attraction are kept as separate groups so the component
 * can filter categories on the landing page while keeping attractions.
 * The dropdown renders both under a shared "Categories" heading.
 */
export const getMergedCombinedGroups = createSelector(
  getCombinedAutocompleteGroups,
  (groups): CombinedSearchGroup[] => {
    const groupOrder: CombinedSearchGroupType[] = [
      "Destination",
      "Attraction",
      "Category",
      "Product",
    ];

    // Deduplicate suggestions across Attraction and Category groups by id
    const seenIds = new Set<string>();

    return groupOrder
      .map((type) => groups.find((g) => g.groupType === type))
      .filter((g): g is CombinedSearchGroup => !!g && g.suggestions.length > 0)
      .map((g) => {
        const uniqueSuggestions = g.suggestions.filter((s) => {
          if (seenIds.has(s.id)) return false;
          seenIds.add(s.id);
          return true;
        });
        return {
          ...g,
          suggestions: uniqueSuggestions.slice(0, MAX_RESULTS_PER_GROUP),
        };
      })
      .filter((g) => g.suggestions.length > 0);
  },
);

export const getIsEntryTypeCrossSell = createSelector(
  getEntryType,
  (entryType) =>
    !!(
      entryType != null &&
      [
        ActivitiesEntryTypeEnum.ACTIVITIES_XSELL,
        ActivitiesEntryTypeEnum.ACTIVITIES_XSELL_CARD,
        ActivitiesEntryTypeEnum.STAYS_XSELL,
        ActivitiesEntryTypeEnum.STAYS_XSELL_CARD,
      ].includes(entryType)
    ),
);

export const getBaseActivitiesProperties = createSelector(
  getTravelWalletCredit,
  getFetchTravelWalletDetailsCallState,
  getRewardsAccounts,
  getEntryType,
  getEntryTypesAll,
  getLodgingId,
  getStaysAdvance,
  getIsEntryTypeCrossSell,
  (
    travelWalletCredit,
    fetchTravelWalletDetailsCallState,
    rewardsAccounts,
    entryType,
    entryTypesAll,
    lodgingId,
    staysAdvance,
    isFromCrossSell,
  ): ITrackingProperties<BaseActivitiesProperties> | null => {
    if (
      fetchTravelWalletDetailsCallState !== CallState.NotCalled &&
      fetchTravelWalletDetailsCallState !== CallState.InProcess
    ) {
      return {
        properties: {
          credit_balance: Math.abs(travelWalletCredit?.amount.amount || 0),
          entry_type: isFromCrossSell ? "cross_sell" : entryType,
          entry_types_all: entryTypesAll,
          rewards_accounts: rewardsAccounts
            .map((r) => r.productDisplayName)
            .join(","),
          lodging_id: lodgingId ?? undefined,
          stays_advance: staysAdvance ?? undefined,
        },
        encryptedProperties: [],
      };
    }
    return null;
  },
);
